import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { Button, ButtonGroup, Form, FormControl, InputGroup, Spinner } from 'react-bootstrap'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faSearch } from '@fortawesome/free-solid-svg-icons'
import { useDebounce } from 'api/hooks/use-debounce'

// Search, Include/Exclude and a checklist of records (vendors, employees) for one filter in the
// AND/OR group builder. Unlike the View's vendor and employee pickers, it saves nothing to the View:
// the choice lives in the leaf, as { selections: [{ id, label }], operator }.
const RecordFilterPopover = ({ title, noun, params, setParams, onClose, onSetHandleApply, canEdit = true, useResults }) => {
  const [selections, setSelections] = useState(params.selections || [])
  const [operator, setOperator] = useState(params.operator || 'include')
  const [search, setSearch] = useState('')
  const debouncedSearch = useDebounce(search, 300)
  const { results, isFetching } = useResults(debouncedSearch.trim())

  const apply = () => {
    setParams((current) => ({ ...current, selections, operator }))
    onClose?.()
  }

  useEffect(() => {
    onSetHandleApply?.(apply)
  }, [selections, operator]) // eslint-disable-line react-hooks/exhaustive-deps

  const selectedIds = selections.map(({ id }) => id)
  const toggle = (record) =>
    setSelections((current) =>
      selectedIds.includes(record.id) ? current.filter(({ id }) => id !== record.id) : [...current, record]
    )

  const check = (record, checked) => (
    <Form.Check
      key={record.id}
      type="checkbox"
      id={`record-filter-${record.id}`}
      className="mb-1"
      label={record.label}
      checked={checked}
      onChange={() => canEdit && toggle(record)}
      disabled={!canEdit}
    />
  )

  return (
    <div style={{ minWidth: 320 }}>
      <div className="mb-2 d-flex justify-content-between align-items-center gap-2">
        <strong>{title}</strong>
        <ButtonGroup size="sm">
          {['include', 'exclude'].map((option) => (
            <Button
              key={option}
              variant={operator === option ? 'primary' : 'light'}
              className={operator === option ? '' : 'text-primary'}
              onClick={() => canEdit && setOperator(option)}
              disabled={!canEdit}
              data-testid={`record-filter-${option}`}
            >
              {option === 'include' ? 'Include' : 'Exclude'}
            </Button>
          ))}
        </ButtonGroup>
      </div>
      <InputGroup size="sm" className="mb-2">
        <InputGroup.Text>
          <FontAwesomeIcon icon={faSearch} />
        </InputGroup.Text>
        <FormControl
          placeholder={`Search ${noun}s`}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          disabled={!canEdit}
          data-testid="record-filter-search"
        />
      </InputGroup>
      <div style={{ maxHeight: 240, overflowY: 'auto' }}>
        {selections.length > 0 && (
          <>
            <small className="text-muted fw-bold d-block mb-1">
              {operator === 'include' ? 'INCLUDED' : 'EXCLUDED'} ({selections.length})
            </small>
            {selections.map((record) => check(record, true))}
            <hr className="my-2" />
          </>
        )}
        {isFetching && <Spinner animation="border" size="sm" />}
        {!isFetching && debouncedSearch.trim().length < 2 && (
          <small className="text-muted">Type at least 2 characters to search {noun}s.</small>
        )}
        {results.filter(({ id }) => !selectedIds.includes(id)).map((record) => check(record, false))}
      </div>
      <div className="mt-3 d-flex justify-content-end">
        <Button variant="primary" size="sm" onClick={canEdit ? apply : onClose}>
          {canEdit ? 'Apply' : 'Close'}
        </Button>
      </div>
    </div>
  )
}

RecordFilterPopover.propTypes = {
  title: PropTypes.string,
  noun: PropTypes.string.isRequired,
  params: PropTypes.object.isRequired,
  setParams: PropTypes.func.isRequired,
  onClose: PropTypes.func,
  onSetHandleApply: PropTypes.func,
  canEdit: PropTypes.bool,
  useResults: PropTypes.func.isRequired
}

export default RecordFilterPopover
